import FeatureGates from '@atlaskit/feature-gate-js-client';
import {
	type DismissCallback,
	useContextScope,
	useMessagePlacementHooks,
} from '@atlassian/post-office-context';
import { MaybeSuspenseBoundary } from '@post-office/placement-common/utils/message-renderer';
import { useInitialData } from '@post-office/placement-contexts/hooks';
import { usePlacementApi } from '@post-office/use-placement-api';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { EXPERIMENTAL_MESSAGES } from './experimental-messages-list';
import { scopes } from './scopes';
import { SuppressIfModalIsPresent } from './suppress-if-modal-is-present';
import { useEpMigrationTriggers } from './use-ep-migration-triggers';
import { MessageRenderer } from '../__generated__/client/render';
import { type ScreenSpaceFlagsMessageState } from '../api/types';
import type { Placement as SSFPlacement } from '../contracts/Placement';
import { usePostOfficeSearchParams } from './use-post-office-search-params';
import { type JSX } from 'react/jsx-runtime';

type PlacementMessageState = {
	messages?: ScreenSpaceFlagsMessageState[];
	externalMessages?: ScreenSpaceFlagsMessageState[];
	triggeredMessages?: ScreenSpaceFlagsMessageState[];
};

export const ScreenSpaceFlagsPlacement = ({
	initialData,
	preloadedInitialData,
	setPreloadedInitialData,
	jiraFlagsService,
	reportExpectedError,
	reportUnexpectedError,
}: SSFPlacement & {
	initialData: Promise<Response | undefined> | undefined;
}): JSX.Element | null => {
	const isEPExternalMessageSSREnabled = FeatureGates.checkGate(
		'post-office_enabled_ep_external_message_ssr',
	);

	const [localMessageState, setLocalMessageState] = useState<PlacementMessageState>(() => {
		const messages = preloadedInitialData?.messages?.length
			? preloadedInitialData.messages
			: undefined;
		const externalMessages =
			isEPExternalMessageSSREnabled && preloadedInitialData?.externalMessages?.length
				? preloadedInitialData.externalMessages
				: undefined;
		const triggeredMessages = preloadedInitialData?.triggeredMessages?.length
			? preloadedInitialData.triggeredMessages
			: undefined;
		return {
			messages,
			externalMessages,
			triggeredMessages,
		};
	});

	// Dismissals are stored locally to keep the local state relatively immutable,
	// except unless explicitly refreshed from the server.
	const [dismissedMessageInstanceIds, setDismissedMessageInstanceIds] = useState<string[]>([]);

	const filterByDismissed = useCallback(
		(list: ScreenSpaceFlagsMessageState[] | undefined) =>
			list?.filter((msg) => !dismissedMessageInstanceIds.includes(msg.messageInstanceId)),
		[dismissedMessageInstanceIds],
	);

	// Filter the messages at runtime to ensure they are always accurate and
	// so we don't need to manage the dismissed messages in the state.
	const { filteredMessages, filteredExternalMessages, filteredTriggeredMessages } = useMemo(
		() => ({
			filteredMessages: filterByDismissed(localMessageState.messages),
			filteredExternalMessages: filterByDismissed(localMessageState.externalMessages),
			filteredTriggeredMessages: filterByDismissed(localMessageState.triggeredMessages),
		}),
		[
			localMessageState.messages,
			localMessageState.externalMessages,
			localMessageState.triggeredMessages,
			filterByDismissed,
		],
	);

	useInitialData(initialData);

	const { onMessageDismiss, onMessageClicked, onMessageSnooze } = useMessagePlacementHooks();

	const handleDismiss = useCallback<DismissCallback>(
		({ messageInstanceId }: { messageInstanceId: string }) => {
			if (messageInstanceId) {
				// dismiss the current message instance
				setDismissedMessageInstanceIds((current) => [...current, messageInstanceId]);
			} else {
				// eslint-disable-next-line no-console
				console.error(
					`A valid messageInstanceId is required to dismiss a message: ${messageInstanceId}`,
				);
			}
		},
		[],
	);

	const onDismissed = useCallback(
		(messageInstanceId: string) => {
			handleDismiss({ messageInstanceId });
		},
		[handleDismiss],
	);

	useEffect(() => onMessageDismiss(handleDismiss), [onMessageDismiss]);
	useEffect(() => onMessageClicked(handleDismiss), [onMessageClicked]);
	useEffect(() => onMessageSnooze(handleDismiss), [onMessageSnooze]);

	useEffect(() => {
		// update the preloaded initial data with the accurate filtered messages
		if (typeof setPreloadedInitialData === 'function') {
			if (isEPExternalMessageSSREnabled) {
				setPreloadedInitialData({
					messages: filteredMessages ?? [],
					externalMessages: filteredExternalMessages ?? [],
					triggeredMessages: filteredTriggeredMessages ?? [],
				});
			} else {
				setPreloadedInitialData({
					messages: filteredMessages ?? [],
					triggeredMessages: filteredTriggeredMessages ?? [],
				});
			}
		}
	}, [
		filteredMessages,
		filteredExternalMessages,
		filteredTriggeredMessages,
		setPreloadedInitialData,
		isEPExternalMessageSSREnabled,
	]);

	const activeScopes = useContextScope({
		scopes,
		treatIntersectionAsSame: true,
	});

	const searchParams = usePostOfficeSearchParams();
	const messagesState = usePlacementApi<{
		messages: ScreenSpaceFlagsMessageState[];
		externalMessages?: ScreenSpaceFlagsMessageState[];
		triggeredMessages?: ScreenSpaceFlagsMessageState[];
	}>('/update-messages', {
		queryParams: {
			...searchParams,
			updateKey: activeScopes.updateKey ?? '',
		},
		requestOptions: { method: 'POST', body: JSON.stringify(activeScopes) },
		throttleTime: 100,
		autoRefetch: {
			isEnabled: FeatureGates.checkGate('post-office-ssf-enable-continuous-auto-refetch')
				? !!activeScopes.updateKey && !filteredMessages?.length
				: !!activeScopes.updateKey && typeof filteredMessages === 'undefined',
			updateKey: activeScopes.updateKey ?? '',
		},
	});

	useEffect(() => {
		const data = messagesState?.data;
		if (!data) return;

		setLocalMessageState((prev) => {
			const messages = data.messages ?? [];
			let selectedMessages: ScreenSpaceFlagsMessageState[] = [];

			if (messages.length > 0) {
				let selectedMessage: ScreenSpaceFlagsMessageState | undefined;

				// New experiments in the screen-space-flags placement must be added to the EXPERIMENTAL_MESSAGES list
				for (const message of messages) {
					const experimentalMessage = EXPERIMENTAL_MESSAGES.find(
						(p) => p.messageTemplateId === message.messageTemplateId,
					);

					if (experimentalMessage) {
						const cohort = FeatureGates.getExperimentValue<string>(
							experimentalMessage.statsigExperiment,
							experimentalMessage.statsigParameterName,
							'not-enrolled',
							{ fireExperimentExposure: true },
						);

						if (experimentalMessage?.statsigAAExperiment) {
							FeatureGates.getExperimentValue<string>(
								experimentalMessage.statsigAAExperiment,
								experimentalMessage.statsigParameterName,
								'not-enrolled',
								{ fireExperimentExposure: true },
							);
						}

						if (experimentalMessage.statsigTestCohorts.includes(cohort)) {
							selectedMessage = message;
							break;
						}
					} else {
						selectedMessage = message;
						break;
					}
				}
				selectedMessages = selectedMessage ? [selectedMessage] : [];
			}

			const externalMessages = data.externalMessages ?? [];
			const triggeredMessages = data.triggeredMessages ?? [];
			return {
				...prev,
				messages: selectedMessages,
				externalMessages,
				triggeredMessages,
			};
		});
	}, [messagesState.data]);

	const { triggerStates: epTriggerStates, EpTriggerWrapper } =
		useEpMigrationTriggers(filteredExternalMessages);

	// find if an EP message has been triggered
	const activeEpMessageInstanceId = Object.keys(epTriggerStates).find(
		(messageInstanceId) => epTriggerStates[messageInstanceId] === true,
	);

	// Check for EP External messages first because they are not rendered until triggered from the front-end
	// and they will not be rendered if a non-ep message is currently rendered due to the choreographer
	const [currentLocalMessage] = filteredMessages ?? [];

	const currentExternalMessage = filteredExternalMessages?.find(
		(msg) => msg.messageInstanceId === activeEpMessageInstanceId,
	);

	const activeExternalMessage = activeEpMessageInstanceId
		? currentExternalMessage
		: currentLocalMessage;

	const messageToRender = currentLocalMessage ?? activeExternalMessage;

	const content = FeatureGates.checkGate('post-office-screen-space-flags-shadow') ? (
		<SuppressIfModalIsPresent>
			{messageToRender ? (
				<MessageRenderer
					{...messageToRender}
					key={messageToRender.messageInstanceId}
					id={messageToRender.messageInstanceId}
					messageInstanceId={messageToRender.messageInstanceId}
					onDismissed={onDismissed}
					SuspenseBoundary={MaybeSuspenseBoundary}
					jiraFlagsService={jiraFlagsService}
					onError={reportUnexpectedError}
					reportExpectedError={reportExpectedError}
				/>
			) : null}
			{filteredTriggeredMessages?.map((msg) => (
				<MessageRenderer
					{...msg}
					key={msg.messageInstanceId}
					id={msg.messageInstanceId}
					messageInstanceId={msg.messageInstanceId}
					onDismissed={onDismissed}
					SuspenseBoundary={MaybeSuspenseBoundary}
					jiraFlagsService={jiraFlagsService}
					onError={reportUnexpectedError}
					reportExpectedError={reportExpectedError}
				/>
			))}
		</SuppressIfModalIsPresent>
	) : null;

	// Wrap content with trigger tracker components
	return <EpTriggerWrapper>{content}</EpTriggerWrapper>;
};
